import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCases, industries, resources } from "../../jobTemplate/components/navdata";
// Footer logo (white text, for the dark footer). If it shows up dark, try
// VIRIQO_HIRES_Logo_white_bg.png instead.
import logo from "../../interviewAI/assets/newlogo/VIRIQO_HIRES_Logo.png";
import "./Footer.css";

const brandName = "Viriqo Hires";

const socialLinks = [
  {
    name: "Meta",
    url: "https://www.facebook.com/vlinkinc",
    stroke: true,
    path: "M3 15.5c0-4.5 2.2-8.5 4.8-8.5 3.4 0 5.9 10 9.4 10 1.9 0 3.3-1.9 3.3-4.8 0-3-1.3-5.2-3.2-5.2-3.4 0-5.9 10-9.6 10C5 17 3 16.6 3 15.5z",
  },
  {
    name: "X",
    url: "https://x.com/VLinkInc",
    path: "M17.8 3h3.1l-6.8 7.8L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.3-8.3L2 3h6.4l4.4 5.8L17.8 3zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5z",
  },
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/company/vlink-inc",
    path: "M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.75h4V21H3V9.75zM9.5 9.75h3.8v1.6h.06c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.77 2.65 4.77 6.1V21h-4v-5c0-1.2-.02-2.73-1.66-2.73-1.67 0-1.92 1.3-1.92 2.64V21h-4V9.75z",
  },
  {
    name: "YouTube",
    url: "https://www.youtube.com/channel/UCIts-lcy_h9hhboDZnroLFg",
    path: "M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12a31 31 0 0 0 .5 4.8 3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8zM9.8 15.1V8.9L15.6 12l-5.8 3.1z",
  },
];

const columns = [
  { key: "useCases", title: "Use Cases", links: useCases },
  { key: "industries", title: "Industries", links: industries },
  { key: "resources", title: "Resources", links: resources },
  {
    key: "company",
    title: "Company",
    links: [
      { label: "About Us", path: "/about" },
      { label: "Sign In", path: "/login" },
    ],
  },
];

const Chevron = () => (
  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Footer = () => {
  const navigate = useNavigate();
  const [openCols, setOpenCols] = useState({});

  // Footer accordions only act on mobile (<= 640px)
  const toggleCol = (key) => {
    if (window.innerWidth > 640) return;
    setOpenCols((cur) => ({ ...cur, [key]: !cur[key] }));
  };

  const handleLogoClick = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Opens the site cookie popup if present, otherwise falls back to the privacy page
  const handleCookieSettings = () => {
    if (typeof window.TTConsent?.open === "function") {
      window.TTConsent.open();
    } else if (typeof window.openCookieSettings === "function") {
      window.openCookieSettings();
    } else {
      navigate("/privacy-policy");
    }
  };

  return (
    <footer className="vh-footer">
      <div className="vh-wrap">
        <div className="vh-f-top">
          <div className="vh-f-brand">
            <Link to="/" aria-label="Viriqo Hires home" onClick={handleLogoClick}>
              <img src={logo} alt="Viriqo Hires" />
            </Link>
            <p>AI-powered interviews for smarter, more trustworthy hiring.</p>
            <div className="vh-social" aria-label="Viriqo Hires on social media">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${brandName} on ${social.name}`}
                >
                  {social.stroke ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d={social.path} />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d={social.path} />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.key} className={`vh-f-col${openCols[col.key] ? " on" : ""}`}>
              <h5>
                <button
                  type="button"
                  aria-expanded={!!openCols[col.key]}
                  onClick={() => toggleCol(col.key)}
                >
                  {col.title} <Chevron />
                </button>
              </h5>
              <div className="vh-f-sub">
                <div>
                  <ul>
                    {col.links.map((link) => (
                      <li key={link.path}>
                        <Link to={link.path}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="vh-f-bot">
          <span>&copy; {new Date().getFullYear()} {brandName}. All rights reserved.</span>
          <div className="vh-f-legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <button type="button" onClick={handleCookieSettings}>Cookie Settings</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
